// src/services/quranApiService.ts
import axios from 'axios'
import { normalizeQuranLangCode, QURAN_LANG_NAME_HINTS } from '@/utils/quranTranslationLanguages'

export interface Surah {
  id: number
  number: number
  name: string
  nameEn: string
  arabicName?: string
  ayahs: number
  revelationPlace: string
  revelationOrder: number
  juz: number
}

export interface Ayah {
  id: number
  number: number
  arabicText: string
  translation?: string | null
  translations?: { [language: string]: string }
  audioUrl?: string
  page: number
  juz: number
}

export interface SurahDetail extends Surah {
  ayahsList: Ayah[]
  audioUrl?: string
}

export interface TranslationLanguage {
  id: number
  name: string
  nativeName: string
  isoCode: string
  direction: 'ltr' | 'rtl'
}

export interface Reciter {
  id: number
  name: string
  nameArabic?: string
  style?: string
}

// For compatibility with existing code
export interface ReciterCompat {
  id: string
  name: string
  nameEn: string
}

const quranHttp = axios.create({
  baseURL: 'https://api.quran.com/api/v4',
})

class QuranApiService {
  /**
   * إرجاع قائمة السور
   * نستخدم /chapters ونفلتر search في الفرونت
   */
  async getSurahs(search?: string): Promise<Surah[]> {
    const res = await quranHttp.get('/chapters', {
      params: { language: 'ar' },
    })

    const chapters = res.data.chapters as any[]

    // Calculate juz for each surah based on verse ranges
    // Juz ranges: 1-7, 8-16, 17-25, 26-34, 35-43, 44-52, 53-60, 61-68, 69-77, 78-85, 86-93, 94-101, 102-110, 111-118, 119-127, 128-136, 137-145, 146-154, 155-163, 164-171, 172-180, 181-188, 189-196, 197-204, 205-212, 213-220, 221-228, 229-236, 237-244, 245-252
    const juzRanges = [
      { start: 1, end: 7, juz: 1 },
      { start: 8, end: 16, juz: 1 },
      { start: 17, end: 25, juz: 1 },
      { start: 26, end: 34, juz: 1 },
      { start: 35, end: 43, juz: 1 },
      { start: 44, end: 52, juz: 1 },
      { start: 53, end: 60, juz: 1 },
      { start: 61, end: 68, juz: 1 },
      { start: 69, end: 77, juz: 1 },
      { start: 78, end: 85, juz: 1 },
      { start: 86, end: 93, juz: 1 },
      { start: 94, end: 101, juz: 1 },
      { start: 102, end: 110, juz: 1 },
      { start: 111, end: 118, juz: 1 },
      { start: 119, end: 127, juz: 1 },
      { start: 128, end: 136, juz: 1 },
      { start: 137, end: 145, juz: 1 },
      { start: 146, end: 154, juz: 1 },
      { start: 155, end: 163, juz: 1 },
      { start: 164, end: 171, juz: 1 },
      { start: 172, end: 180, juz: 1 },
      { start: 181, end: 188, juz: 1 },
      { start: 189, end: 196, juz: 1 },
      { start: 197, end: 204, juz: 1 },
      { start: 205, end: 212, juz: 1 },
      { start: 213, end: 220, juz: 1 },
      { start: 221, end: 228, juz: 1 },
      { start: 229, end: 236, juz: 1 },
      { start: 237, end: 244, juz: 1 },
      { start: 245, end: 252, juz: 1 },
    ]
    
    // Better approach: Get juz from API or calculate from first verse
    const getJuzForSurah = async (surahNumber: number): Promise<number> => {
      try {
        // Get first verse of surah to determine juz
        const verseRes = await quranHttp.get('/quran/verses/uthmani', {
          params: {
            chapter_number: surahNumber,
            page: 1,
            per_page: 1,
          },
        })
        
        if (verseRes.data.verses && verseRes.data.verses.length > 0) {
          const firstVerse = verseRes.data.verses[0]
          return firstVerse.juz_number || 1
        }
      } catch (err) {
        console.warn(`Failed to get juz for surah ${surahNumber}:`, err)
      }
      return 1
    }
    
    // Map surahs with juz calculation
    let surahs: Surah[] = await Promise.all(
      chapters.map(async (c) => {
        const juz = await getJuzForSurah(c.id)
        return {
          id: c.id,
          number: c.id,
          name: c.name_arabic,
          nameEn: c.name_simple,
          arabicName: c.name_arabic,
          ayahs: c.verses_count,
          revelationPlace:
            c.revelation_place?.toLowerCase() === 'makkah' ? 'Mecca' : 'Medina',
          revelationOrder: c.revelation_order,
          juz: juz,
        }
      })
    )

    if (search && search.trim()) {
      const q = search.trim().toLowerCase()
      surahs = surahs.filter(
        (s) =>
          s.number.toString() === q ||
          s.name.toLowerCase().includes(q) ||
          (s.nameEn && s.nameEn.toLowerCase().includes(q))
      )
    }

    return surahs
  }

  /**
   * إرجاع قائمة اللغات المتاحة للترجمة
   * Returns array of language codes for compatibility with existing code
   */
  async getTranslationLanguages(): Promise<string[]> {
    try {
      const res = await quranHttp.get('/resources/translations')
      const languages = res.data.translations.map((t: any) => {
        const raw = t.iso_code || t.language_name?.toLowerCase().split(/\s+/)[0] || 'en'
        return normalizeQuranLangCode(String(raw))
      })
      return [...new Set(languages)]
    } catch (error) {
      console.error('Failed to fetch translation languages:', error)
      // Return default languages
      return ['en', 'ar', 'fr', 'tr', 'ur', 'ms', 'id', 'bn', 'ru', 'zh', 'fa']
    }
  }

  /**
   * إرجاع قائمة اللغات المتاحة للترجمة مع التفاصيل الكاملة
   */
  async getTranslationLanguagesDetailed(): Promise<TranslationLanguage[]> {
    try {
      const res = await quranHttp.get('/resources/translations')
      return res.data.translations.map((t: any) => ({
        id: t.id,
        name: t.name,
        nativeName: t.language_name,
        isoCode: t.iso_code || t.language_name?.toLowerCase().split(' ')[0] || 'en',
        direction: t.direction || 'ltr',
      }))
    } catch (error) {
      console.error('Failed to fetch translation languages:', error)
      // Return default languages
      return [
        { id: 1, name: 'English', nativeName: 'English', isoCode: 'en', direction: 'ltr' },
        { id: 2, name: 'Arabic', nativeName: 'العربية', isoCode: 'ar', direction: 'rtl' },
        { id: 3, name: 'French', nativeName: 'Français', isoCode: 'fr', direction: 'ltr' },
        { id: 4, name: 'Turkish', nativeName: 'Türkçe', isoCode: 'tr', direction: 'ltr' },
        { id: 5, name: 'Urdu', nativeName: 'اردو', isoCode: 'ur', direction: 'rtl' },
      ]
    }
  }

  /**
   * إرجاع قائمة القراء
   * Returns array compatible with existing code
   */
  async getReciters(): Promise<ReciterCompat[]> {
    try {
      const res = await quranHttp.get('/resources/recitations')
      return res.data.recitations.map((r: any) => ({
        id: r.id.toString(),
        name: r.reciter_name || r.translated_name?.name || r.style || 'Unknown',
        nameEn: r.translated_name?.name || r.style || r.reciter_name || 'Unknown',
      }))
    } catch (error) {
      console.error('Failed to fetch reciters:', error)
      return []
    }
  }

  /**
   * إرجاع قائمة القراء مع التفاصيل الكاملة
   */
  async getRecitersDetailed(): Promise<Reciter[]> {
    try {
      const res = await quranHttp.get('/resources/recitations')
      return res.data.recitations.map((r: any) => ({
        id: r.id,
        name: r.translated_name?.name || r.style || r.reciter_name,
        nameArabic: r.reciter_name,
        style: r.style,
      }))
    } catch (error) {
      console.error('Failed to fetch reciters:', error)
      return []
    }
  }

  /**
   * إرجاع تفاصيل سورة (نص الآيات) مع الترجمات والصوتيات
   */
  async getSurah(surahNumber: number, lang = 'en', translationId?: number): Promise<SurahDetail> {
    // 1) بيانات السورة
    const chapterRes = await quranHttp.get(`/chapters/${surahNumber}`, {
      params: { language: lang === 'ar' ? 'ar' : 'en' },
    })
    const c = chapterRes.data.chapter

    // 2) كل الآيات (loop على pages)
    let page = 1
    const allVerses: any[] = []

    while (true) {
      const versesRes = await quranHttp.get('/quran/verses/uthmani', {
        params: {
          chapter_number: surahNumber,
          page,
          per_page: 50,
          fields: 'text_uthmani,verse_key,verse_number,page_number,juz_number', // Request specific fields
        },
      })

      const { verses, pagination } = versesRes.data
      
      // Debug: check verse structure
      if (page === 1 && verses && verses.length > 0) {
        console.log('🔍 Sample verse structure from API:', verses[0])
        console.log('🔍 Verse keys:', Object.keys(verses[0]))
        console.log('🔍 Page number in verse:', {
          page_number: verses[0].page_number,
          page: verses[0].page,
          pageNumber: verses[0].pageNumber
        })
      }
      
      allVerses.push(...verses)

      if (!pagination?.next_page) break
      page = pagination.next_page
    }

    // 2.5) Check if page_number is missing and try to fetch it
    // First, check if any verse has page_number
    const hasPageNumbers = allVerses.some(v => v.page_number || v.page || v.pageNumber)
    
    let versesWithPageNumbers = allVerses
    
    // Only fetch page numbers if they're missing
    if (!hasPageNumbers && allVerses.length > 0) {
      console.log('⚠️ page_number not found in API response, attempting to fetch...')
      
      // Try to fetch page numbers in batches (more efficient)
      // For now, just use the verses as-is and set default page to 1
      // The actual page numbers will be calculated based on the surah's position
      versesWithPageNumbers = allVerses.map((v, index) => {
        // For now, we'll use a simple calculation or fetch from a different endpoint
        // This is a placeholder - in production, you might want to use a lookup table
        return {
          ...v,
          page_number: v.page_number || v.page || v.pageNumber || 1
        }
      })
      
      console.log('📄 Using default page numbers. Consider implementing proper page number lookup.')
    } else {
      versesWithPageNumbers = allVerses
    }

    // 3) جلب الترجمات إذا كانت اللغة غير عربية
    let translationsMap: { [verseKey: string]: string } = {}
    if (lang !== 'ar') {
      try {
        // Get available translations first if translationId not provided
        let transId = translationId
        if (!transId) {
          const transLangsRes = await quranHttp.get('/resources/translations')
          const translations = transLangsRes.data.translations || []
          
          console.log(`Looking for translation for language: ${lang}`)
          console.log(`Available translations:`, translations.map((t: any) => ({ 
            id: t.id, 
            name: t.name, 
            language_name: t.language_name, 
            iso_code: t.iso_code 
          })))
          
          const langNorm = normalizeQuranLangCode(lang)
          const nameHints = QURAN_LANG_NAME_HINTS[langNorm] || []

          // Find translation matching the language (ISO + optional name hints for African etc.)
          const matchingTrans = translations.find((t: any) => {
            const tLang = (t.language_name || '').toLowerCase()
            const tIso = normalizeQuranLangCode(t.iso_code || '')
            const tName = (t.name || '').toLowerCase()

            if (tIso === langNorm) return true
            if (nameHints.some((h) => tLang.includes(h) || tName.includes(h))) return true

            return (
              tLang.includes(langNorm) ||
              tLang === langNorm ||
              tName.includes(langNorm) ||
              (langNorm === 'en' &&
                (tLang.includes('english') || tName.includes('english') || tIso === 'en'))
            )
          })
          
          if (matchingTrans) {
            transId = matchingTrans.id
            console.log(
              `Found translation for ${lang} (norm=${langNorm}): ID ${transId}, Name: ${matchingTrans.name || matchingTrans.language_name}`,
            )
          } else if (translations.length > 0) {
            // Fallback to first English translation if available
            const englishTrans = translations.find((t: any) => {
              const tLang = (t.language_name || '').toLowerCase()
              const tName = (t.name || '').toLowerCase()
              const tIso = normalizeQuranLangCode(t.iso_code || '')
              return tLang.includes('english') || tName.includes('english') || tIso === 'en'
            })
            transId = englishTrans?.id || translations[0].id
            console.log(`Using fallback translation: ID ${transId}`)
          }
        }

        if (transId) {
          let transPage = 1
          const allTranslations: any[] = []
          
          // Fetch all translation pages
          while (true) {
            try {
              const transRes = await quranHttp.get(`/quran/translations/${transId}`, {
                params: {
                  chapter_number: surahNumber,
                  page: transPage,
                  per_page: 50,
                },
              })

              console.log(`Translation response for page ${transPage}:`, transRes.data)

              // Handle different response structures
              let translations = transRes.data.translations
              if (!translations && transRes.data.data) {
                translations = transRes.data.data.translations || transRes.data.data
              }
              
              // Also check if translations are directly in the response
              if (!translations && Array.isArray(transRes.data)) {
                translations = transRes.data
              }
              
              if (translations && Array.isArray(translations) && translations.length > 0) {
                console.log(`Found ${translations.length} translations on page ${transPage}`)
                allTranslations.push(...translations)
              } else if (translations && !Array.isArray(translations)) {
                // If it's an object, try to extract translations
                Object.values(translations).forEach((t: any) => {
                  if (t && typeof t === 'object' && (t.text || t.translation || t.content)) {
                    allTranslations.push(t)
                  }
                })
              } else {
                console.warn(`No translations found in response for page ${transPage}`)
              }

              const pagination = transRes.data.pagination || transRes.data.data?.pagination
              if (!pagination?.next_page) break
              transPage = pagination.next_page
            } catch (pageError: any) {
              console.warn(`Failed to fetch translation page ${transPage}:`, pageError?.response?.data || pageError.message)
              // Try alternative endpoint
              if (transPage === 1) {
                try {
                  const altRes = await quranHttp.get(`/quran/translations/${transId}`, {
                    params: {
                      chapter_number: surahNumber,
                    },
                  })
                  const altTranslations = altRes.data.translations || altRes.data.data?.translations || []
                  if (altTranslations.length > 0) {
                    allTranslations.push(...altTranslations)
                  }
                } catch (altError) {
                  console.warn('Alternative translation fetch failed:', altError)
                }
              }
              break
            }
          }

          // Map translations by verse number
          if (allTranslations.length > 0) {
            console.log('🔍 Sample translation object structure:', allTranslations[0])
            console.log('🔍 All translation keys:', Object.keys(allTranslations[0]))
          }
          
          // Process translations from API
          // API response structure: { resource_id: 85, text: "...", verse_key: "2:1" }
          allTranslations.forEach((t: any, index: number) => {
            // Extract verse number from verse_key (format: "2:1" -> verse 1)
            let verseNum: number | null = null
            
            if (t.verse_key) {
              // verse_key format: "chapter:verse" (e.g., "2:1")
              const parts = String(t.verse_key).split(':')
              if (parts.length === 2) {
                const verseNumStr = parts[1]
                verseNum = parseInt(verseNumStr, 10)
                if (isNaN(verseNum)) {
                  verseNum = null
                }
              }
            }
            
            // Fallback: try other properties
            if (!verseNum) {
              verseNum = t.verse_number || 
                        t.verseNumber || 
                        t.number || 
                        null
            }
            
            // Final fallback: use index + 1 (translations are ordered)
            if (!verseNum) {
              verseNum = index + 1
            }
            
            // Get translation text
            const text = t.text || 
                        t.translation || 
                        t.content ||
                        t.translated_text ||
                        ''
            
            if (text && text.trim() && verseNum && verseNum > 0) {
              // Ensure verseNum is a number
              const verseNumInt = typeof verseNum === 'string' ? parseInt(verseNum, 10) : verseNum
              if (!isNaN(verseNumInt) && verseNumInt > 0) {
                const verseKey = `${surahNumber}:${verseNumInt}`
                translationsMap[verseKey] = String(text).trim()
                
                // Debug first few mappings
                if (verseNumInt <= 3) {
                  console.log(`✅ Mapped translation: ${verseKey} (from verse_key: ${t.verse_key || 'N/A'}, verseNum: ${verseNum}) = "${text.substring(0, 50)}..."`)
                }
              } else {
                if (index < 3) {
                  console.warn(`⚠️ Invalid verseNum after conversion:`, { verseNum, verseNumInt, text: text.substring(0, 30) })
                }
              }
            } else {
              if (index < 3) {
                console.warn(`⚠️ Missing data for translation ${index + 1}:`, { 
                  verse_key: t.verse_key,
                  verseNum, 
                  text: text ? text.substring(0, 30) : 'missing',
                  fullObject: t 
                })
              }
            }
          })
          
          console.log(`✅ Loaded ${allTranslations.length} translations for surah ${surahNumber}, mapped ${Object.keys(translationsMap).length} verses`)
          if (Object.keys(translationsMap).length > 0) {
            console.log('📋 Sample mapped keys:', Object.keys(translationsMap).slice(0, 5))
          }
        } else {
          console.warn(`No translation ID found for language: ${lang}`)
        }
      } catch (error) {
        console.error('Failed to fetch translations:', error)
      }
    }

    // 4) جلب الصوتيات (default reciter)
    let surahAudioUrl: string | undefined
    try {
      const recitationsRes = await quranHttp.get('/resources/recitations')
      const recitations = recitationsRes.data.recitations || []
      
      if (recitations.length > 0) {
        // Try to find a default reciter (usually ID 1 or first one)
        const defaultReciter = recitations.find((r: any) => r.id === 1) || recitations[0]
        
        if (defaultReciter) {
          try {
            const audioRes = await quranHttp.get(`/chapter_recitations/${defaultReciter.id}/${surahNumber}`)
            surahAudioUrl = audioRes.data.audio_file?.audio_url || audioRes.data.audio_file?.url
            
            if (!surahAudioUrl && audioRes.data.audio_file) {
              // Try alternative paths
              surahAudioUrl = audioRes.data.audio_file.url || audioRes.data.audio_file.audioUrl
            }
            
            console.log(`Loaded audio for surah ${surahNumber} from reciter ${defaultReciter.id}`)
          } catch (audioError) {
            console.warn(`Failed to fetch audio for reciter ${defaultReciter.id}:`, audioError)
            // Try alternative endpoint
            try {
              const altAudioRes = await quranHttp.get(`/quran/verses/audio`, {
                params: {
                  recitation: defaultReciter.id,
                  chapter_number: surahNumber,
                  fields: 'audio_url',
                },
              })
              if (altAudioRes.data.verses && altAudioRes.data.verses.length > 0) {
                surahAudioUrl = altAudioRes.data.verses[0].audio_url
              }
            } catch (altError) {
              console.warn('Alternative audio fetch also failed:', altError)
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch surah audio:', error)
    }

    // 5) بناء قائمة الآيات مع الترجمات
    console.log(`🔧 Building ayahsList: ${allVerses.length} verses, ${Object.keys(translationsMap).length} translations in map`)
    if (Object.keys(translationsMap).length > 0) {
      console.log('📋 Sample translation keys:', Object.keys(translationsMap).slice(0, 5))
    }
    
    // Debug: check first verse structure
    if (allVerses.length > 0) {
      console.log('🔍 First verse from allVerses:', allVerses[0])
      console.log('🔍 First verse keys:', Object.keys(allVerses[0]))
    }
    
    const ayahsList: Ayah[] = versesWithPageNumbers.map((v, index) => {
      // Try multiple possible properties for verse number
      const verseNumber = v.verse_number || 
                         v.verseNumber || 
                         v.number || 
                         v.verse_key?.split(':')[1] ||
                         (v.verse_key ? parseInt(v.verse_key.split(':')[1], 10) : null) ||
                         (index + 1) // Fallback to index + 1
      
      // Try multiple possible properties for Arabic text
      const arabicText = v.text_uthmani || 
                        v.text || 
                        v.arabic_text ||
                        v.text_imlaei ||
                        ''
      
      const verseKey = `${surahNumber}:${verseNumber}`
      const translation = translationsMap[verseKey] || null
      
      // Debug first few ayahs
      if (index < 3) {
        console.log(`🔍 Ayah ${verseNumber} (index ${index}):`, {
          'verseNumber source': v.verse_number ? 'verse_number' : v.verseNumber ? 'verseNumber' : v.number ? 'number' : v.verse_key ? 'verse_key' : 'index+1',
          verseKey,
          'translation in map': !!translationsMap[verseKey],
          translation: translation ? `"${translation.substring(0, 30)}..."` : 'null',
          'all map keys for this surah': Object.keys(translationsMap).filter(k => k.startsWith(`${surahNumber}:`)).slice(0, 5)
        })
      }
      
      // Get page number - try multiple possible properties
      const pageNumber = v.page_number || 
                        v.page || 
                        v.pageNumber ||
                        (v.verse_key ? undefined : undefined) || // Will fetch separately if needed
                        1 // Fallback to 1 if not found
      
      // Debug page numbers for first few ayahs
      if (index < 3) {
        console.log(`📄 Ayah ${verseNumber} page info:`, {
          'page_number': v.page_number,
          'page': v.page,
          'pageNumber': v.pageNumber,
          'final page': pageNumber
        })
      }
      
      return {
        id: v.id || index + 1,
        number: verseNumber,
        arabicText: arabicText,
        translation: translation,
        translations: translation ? { [lang]: translation } : undefined,
        page: pageNumber,
        juz: v.juz_number || v.juz || 1,
      }
    })
    
    const ayahsWithTranslations = ayahsList.filter(a => a.translation).length
    console.log(`✅ Built ${ayahsList.length} ayahs, ${ayahsWithTranslations} with translations (${Object.keys(translationsMap).length} total in map)`)
    
    // Debug: show first few ayahs with their translations
    if (ayahsWithTranslations > 0) {
      console.log('📋 Sample ayahs with translations:', ayahsList.slice(0, 3).map(a => ({
        number: a.number,
        hasTranslation: !!a.translation,
        translationPreview: a.translation ? a.translation.substring(0, 50) : 'none'
      })))
    } else {
      console.warn('⚠️ No ayahs have translations! Check translationsMap mapping')
    }

    // Get juz from first verse - it should be in the verse data
    const firstVerse = allVerses.length > 0 ? allVerses[0] : null
    const surahJuz = firstVerse?.juz_number || 
                    firstVerse?.juz || 
                    c.juz_number || 
                    (allVerses.find(v => v.juz_number)?.juz_number) ||
                    1
    
    console.log(`📖 Surah ${surahNumber} juz: ${surahJuz} (from first verse: ${firstVerse?.juz_number || 'N/A'})`)
    
    const surahBase: Surah = {
      id: c.id,
      number: c.id,
      name: c.name_arabic,
      nameEn: c.name_simple,
      arabicName: c.name_arabic,
      ayahs: c.verses_count,
      revelationPlace:
        c.revelation_place?.toLowerCase() === 'makkah' ? 'Mecca' : 'Medina',
      revelationOrder: c.revelation_order,
      juz: surahJuz,
    }

    return {
      ...surahBase,
      ayahsList,
      audioUrl: surahAudioUrl,
    }
  }

  /**
   * جلب ترجمة سورة بلغة محددة
   */
  async getSurahWithTranslation(surahNumber: number, translationId: number): Promise<SurahDetail> {
    const surah = await this.getSurah(surahNumber, 'en', translationId)
    return surah
  }

  /**
   * جلب صوت سورة بقارئ محدد
   * Returns array of tracks for compatibility with existing code
   */
  async getSurahAudio(surahNumber: number, reciterId: number | string): Promise<{ id: string; url: string; quality: number; reciterId: string }[]> {
    try {
      const reciterIdNum = typeof reciterId === 'string' ? parseInt(reciterId, 10) : reciterId
      const reciterIdStr = reciterIdNum.toString()
      
      if (isNaN(reciterIdNum)) {
        console.error('Invalid reciter ID:', reciterId)
        return []
      }

      let audioUrl = ''
      
      // Try primary endpoint
      try {
        const res = await quranHttp.get(`/chapter_recitations/${reciterIdNum}/${surahNumber}`)
        audioUrl = res.data.audio_file?.audio_url || 
                   res.data.audio_file?.url || 
                   res.data.audio_url ||
                   res.data.url ||
                   ''
      } catch (primaryError) {
        console.warn('Primary audio endpoint failed, trying alternative:', primaryError)
        
        // Try alternative endpoint - get first verse audio
        try {
          const altRes = await quranHttp.get('/quran/verses/audio', {
            params: {
              recitation: reciterIdNum,
              chapter_number: surahNumber,
              fields: 'audio_url',
            },
          })
          
          if (altRes.data.verses && altRes.data.verses.length > 0) {
            // For full surah, we might need to construct URL or use first verse
            audioUrl = altRes.data.verses[0].audio_url || ''
            
            // If we only got one verse, try to get full surah URL
            if (audioUrl && !audioUrl.includes('full')) {
              // Try to construct full surah URL from verse URL pattern
              const verseUrlMatch = audioUrl.match(/(.*\/)(\d+)\/(\d+)\.mp3/)
              if (verseUrlMatch) {
                // This is a verse URL, we need full surah URL
                // Try direct chapter_recitations again with different format
                try {
                  const fullRes = await quranHttp.get(`/chapter_recitations/${reciterIdNum}/${surahNumber}`, {
                    params: { format: 'mp3' }
                  })
                  audioUrl = fullRes.data.audio_file?.audio_url || audioUrl
                } catch {
                  // Keep verse URL as fallback
                }
              }
            }
          }
        } catch (altError) {
          console.error('Alternative audio endpoint also failed:', altError)
        }
      }
      
      if (!audioUrl) {
        console.warn(`No audio URL found for surah ${surahNumber}, reciter ${reciterIdNum}`)
        return []
      }

      return [{
        id: `${surahNumber}-${reciterIdNum}`,
        url: audioUrl,
        quality: 128, // Default quality
        reciterId: reciterIdStr,
      }]
    } catch (error: any) {
      console.error('Failed to fetch surah audio:', error?.response?.data || error.message)
      return []
    }
  }

  /**
   * جلب صوت آية محددة
   */
  async getAyahAudio(surahNumber: number, ayahNumber: number, reciterId: number | string): Promise<{ audioUrl: string }> {
    try {
      const reciterIdNum = typeof reciterId === 'string' ? parseInt(reciterId, 10) : reciterId
      const res = await quranHttp.get('/quran/verses/audio', {
        params: {
          recitation: reciterIdNum,
          chapter_number: surahNumber,
          fields: 'audio_url',
        },
      })

      const verse = res.data.verses.find((v: any) => 
        v.verse_key === `${surahNumber}:${ayahNumber}`
      )

      if (!verse) {
        throw new Error('Ayah audio not found')
      }

      return {
        audioUrl: verse.audio_url || '',
      }
    } catch (error) {
      console.error('Failed to fetch ayah audio:', error)
      throw error
    }
  }

  /**
   * جلب ترجمة آية محددة
   */
  async getAyahTranslation(surahNumber: number, ayahNumber: number, translationId: number): Promise<string | null> {
    try {
      const res = await quranHttp.get(`/quran/translations/${translationId}`, {
        params: {
          chapter_number: surahNumber,
          verse_number: ayahNumber,
        },
      })

      const translation = res.data.translations.find((t: any) => 
        t.verse_key === `${surahNumber}:${ayahNumber}`
      )

      return translation?.text || null
    } catch (error) {
      console.error('Failed to fetch ayah translation:', error)
      return null
    }
  }

  /**
   * جلب قارئ محدد
   */
  async getReader(surahNumber: number, lang: string = 'en'): Promise<any> {
    // This method can be used for reader view
    return this.getSurah(surahNumber, lang)
  }

  /**
   * جلب التفسير
   */
  async getTafsir(surahNumber: number, tafsirId: string): Promise<any> {
    try {
      // Get all ayahs first
      const surah = await this.getSurah(surahNumber, 'ar')
      const ayahs = surah.ayahsList

      // Fetch tafsir for each ayah
      const tafsirPromises = ayahs.map(async (ayah) => {
        try {
          const res = await quranHttp.get(`/tafsir/${tafsirId}`, {
            params: {
              chapter_number: surahNumber,
              verse_number: ayah.number,
            },
          })
          return {
            number: ayah.number,
            text: res.data.tafsir?.text || null,
          }
        } catch {
          return {
            number: ayah.number,
            text: null,
          }
        }
      })

      const tafsirResults = await Promise.all(tafsirPromises)
      return {
        surahNumber,
        ayahs: tafsirResults.filter((t) => t.text !== null),
      }
    } catch (error) {
      console.error('Failed to fetch tafsir:', error)
      return { surahNumber, ayahs: [] }
    }
  }
}

export const quranApi = new QuranApiService()
